import { DeviceNotification } from './DeviceNotification';
import { NOTIFICATION_PRIORITIES } from './device_notifications_priorities';

/**
 * Type of the notification.
 * Refers to the typeof the Alert.
 */
export type NotificationType = 'success' | 'danger' | 'warning' | 'info' | 'promo';

/**
 * Structure of the CTA (Call to Action) of the notification.
 * @param {string} title - Title of the CTA.
 * @param {function} onClick - Function to be executed when the CTA is clicked.
 */
export type CTAType = {
	title: string;
	onClick?: (...args: any[]) => string | void | Promise<void>;
};

/**
 * Builder class for the Device Notification.
 *
 * We use the builder class to create the Device Notification object, while
 * the Device Notification object is encapsulated in @DeviceNotification class.
 */
export class DeviceNotificationBuilder {
	private id: string;
	private type: NotificationType;
	private dismissable: boolean = true;
	private priority: number;
	private heading?: {
		global: string;
		local: string;
	};
	private message?: string;
	private messageHighlight?: string;
	private ctaGlobal?: CTAType;
	private ctaLocal?: CTAType;
	private error?: string;

	constructor(id: string, type: NotificationType) {
		this.id = id;
		this.type = type;
		// Extract notification key from id (format: deviceId-notificationType)
		const notifKey = id.split('-').slice(1).join('-');
		// Assign priority based on the notification key
		this.priority = NOTIFICATION_PRIORITIES[notifKey] ?? NOTIFICATION_PRIORITIES['default'];
	}

	/**
	 * If the notification can be dismissed.
	 * @param dismissable boolean
	 */
	setDismissable(dismissable: boolean): DeviceNotificationBuilder {
		this.dismissable = dismissable;
		return this;
	}

	/**
	 * Set the heading of the notification.
	 * @param heading {local: string, global: string}
	 */
	setHeading(heading: { global: string; local: string }): DeviceNotificationBuilder {
		this.heading = heading;
		return this;
	}

	/**
	 * Set notification message.
	 * @param message string
	 */
	setMessage(message: string): DeviceNotificationBuilder {
		this.message = message;
		return this;
	}

	/**
	 * Set an optional bold highlight shown in the notification message body.
	 * @param messageHighlight string
	 */
	setMessageHighlight(messageHighlight: string): DeviceNotificationBuilder {
		this.messageHighlight = messageHighlight;
		return this;
	}

	/**
	 * Set the global CTA (Call to Action) of the notification.
	 * @param cta {title: string, onClick: (...args: any[]) => string | void}
	 */
	setCtaGlobal(cta: CTAType): DeviceNotificationBuilder {
		this.ctaGlobal = cta;
		return this;
	}

	/**
	 * Set the local CTA (Call to Action) of the notification.
	 * @param cta {title: string, onClick: (...args: any[]) => string | void}
	 */
	setCtaLocal(cta: CTAType): DeviceNotificationBuilder {
		this.ctaLocal = cta;
		return this;
	}

	/**
	 * Set error type of the notification.
	 * @param error string
	 */
	setError(error: string): DeviceNotificationBuilder {
		this.error = error;
		return this;
	}

	getId(): string {
		return this.id;
	}

	getType(): NotificationType {
		return this.type;
	}

	getDismissable(): boolean | undefined {
		return this.dismissable;
	}

	getHeading(): { global: string; local: string } | undefined {
		return this.heading;
	}

	getMessage(): string | undefined {
		return this.message;
	}

	getMessageHighlight(): string | undefined {
		return this.messageHighlight;
	}

	getCtaGlobal(): CTAType | undefined {
		return this.ctaGlobal;
	}

	getCtaLocal(): CTAType | undefined {
		return this.ctaLocal;
	}

	getError(): string | undefined {
		return this.error;
	}

	getPriority(): number {
		return this.priority;
	}

	build(): DeviceNotification {
		return new DeviceNotification(this);
	}
}
